import type { HereGeocodeItem } from '@sbt-web/networking';
import { Palette } from '@sbt-web/ui';
import classnames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import loadHereCode from './here-loader';
import ownClasses from './FilterMap.module.css';
declare const window: typeof globalThis & typeof Window & { H?: typeof H };

interface Props {
  selectedPoint?: Pick<HereGeocodeItem, 'position'>;
  radius: number;
}

const centreOfItaly = { lat: 41.87194, lng: 12.56738 };
const defaultZoom = 4.2;

const HereMap = ({ selectedPoint, radius }: Props) => {
  // Create a reference to the HTML element we want to put the map on
  const mapRef = useRef<HTMLDivElement>(null);
  const [hereCodeLoaded, setHereCodeLoaded] = useState(false);

  // Load the SDK from HERE
  useEffect(() => {
    loadHereCode(setHereCodeLoaded);
  }, [setHereCodeLoaded]);

  const [hereMap, setHereMap] = useState<H.Map | null>(null);

  // Create the map, if the SDK code is ready
  useLayoutEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (hereCodeLoaded) {
      const H = window.H;
      const platform = new H.service.Platform({
        apikey: process.env.NEXT_PUBLIC_HERE_WEB_KEY,
      });
      const defaultLayers = platform.createDefaultLayers({
        ppi: 250,
        lg: 'it-IT',
        crossOrigin: 'anonymous',
      });

      const hm = new H.Map(mapRef.current, defaultLayers.vector.normal.map, {
        padding: {
          top: 24,
          right: 24,
          bottom: 24,
          left: 24,
        },
        pixelRatio: window.devicePixelRatio || 1,
        center: centreOfItaly,
        zoom: defaultZoom,
      });

      setHereMap(hm);

      // This will act as a cleanup to run once this hook runs again.
      // This includes when the component un-mounts
      return () => {
        hm.dispose();
      };
    }
  }, [hereCodeLoaded, setHereMap]);

  // Set the center and marker on the map
  useEffect(() => {
    if (hereMap == null) {
      return;
    } else if (
      selectedPoint == null ||
      selectedPoint.position?.lat == null ||
      selectedPoint.position?.lng == null
    ) {
      hereMap?.setCenter(centreOfItaly).setZoom(defaultZoom);
      return;
    }

    hereMap.setCenter({
      lat: selectedPoint.position.lat,
      lng: selectedPoint.position.lng,
    });
  }, [hereMap, selectedPoint]);

  // Draw the radius and zoom to see the entire circle.
  useEffect(() => {
    if (
      hereMap == null ||
      selectedPoint == null ||
      selectedPoint.position?.lat == null ||
      selectedPoint.position?.lng == null
    ) {
      return;
    }

    const circle = new window.H.map.Circle(
      // The central point of the circle
      { lat: selectedPoint.position.lat, lng: selectedPoint.position.lng },
      // The radius of the circle in meters
      radius,
      {
        style: {
          lineWidth: 2,
          strokeColor: Palette.PurpleImmobili, // Color of the perimeter
          fillColor: 'rgba(162, 89, 255, 0.1)', // Color of the circle
        },
      }
    );

    hereMap.addObject(circle);

    hereMap.getViewModel().setLookAtData({
      bounds: circle.getBoundingBox(),
    });

    return () => {
      if (hereMap?.getObjects().includes(circle)) {
        hereMap.removeObject(circle);
      }
    };
  }, [hereMap, radius, selectedPoint]);

  return <div className={classnames(ownClasses['here-map'])} ref={mapRef} />;
};

export default HereMap;
